import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    padding: '20px 0',
  },
  scroller: {
    display: 'flex',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    padding: '20px 0',
    width: '100%',
  },
  card: {
    width: 'calc(25% - 20px)',
    maxWidth: 'calc(25% - 20px)',
    margin: '0 10px',
    flex: '0 0 calc(25% - 20px)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 320,
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    },
    '@media (max-width: 1400px)': {
      flex: '0 0 calc(33.333% - 20px)',
      maxWidth: 'calc(33.333% - 20px)',
    },
    '@media (max-width: 960px)': {
      flex: '0 0 calc(50% - 20px)',
      maxWidth: 'calc(50% - 20px)',
    },
    '@media (max-width: 600px)': {
      flex: '0 0 calc(100% - 20px)',
      maxWidth: 'calc(100% - 20px)',
    },
  },
  media: {
    height: 140,
    flexShrink: 0,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  description: {
    marginTop: theme.spacing(1),
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  },
  navigationButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    zIndex: 1,
  },
  leftButton: {
    left: theme.spacing(1),
  },
  rightButton: {
    right: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
}));

interface Template {
  title: string;
  description: string;
  imageUrl: string;
  onClick: () => void;
  tags?: string[];
}

interface TemplateScrollerProps {
  title: string;
  templates: Template[];
}

export const TemplateScroller = ({ title, templates }: TemplateScrollerProps) => {
  const classes = useStyles();
  const scrollerRef = React.useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollerRef.current) {
      const scrollAmount = scrollerRef.current.clientWidth || 800;
      scrollerRef.current.scrollLeft += direction === 'left' ? -scrollAmount : scrollAmount;
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        {title}
      </Typography>
      {templates.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <IconButton
            className={`${classes.navigationButton} ${classes.leftButton}`}
            onClick={() => scroll('left')}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            className={`${classes.navigationButton} ${classes.rightButton}`}
            onClick={() => scroll('right')}
          >
            <ChevronRight />
          </IconButton>
          <div className={classes.scroller} ref={scrollerRef}>
            {templates.map((template, index) => (
              <Card key={index} className={classes.card} onClick={template.onClick}>
                <CardMedia
                  className={classes.media}
                  image={template.imageUrl}
                  title={template.title}
                />
                <CardContent className={classes.cardContent}>
                  <Typography gutterBottom variant="h6" component="h2">
                    {template.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="p"
                    className={classes.description}
                  >
                    {template.description}
                  </Typography>
                  {template.tags && (
                    <div style={{ marginTop: '8px' }}>
                      {template.tags.map(tag => (
                        <Typography
                          key={tag}
                          variant="caption"
                          style={{
                            backgroundColor: '#eee',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            marginRight: '4px',
                          }}
                        >
                          {tag}
                        </Typography>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}; 
